/*
 * Ryan O'Dowd
 * 2025-07-31
 * © Copyright 2025 Oakwood Software Consulting, Inc. All Rights Reserved.
 */
import React, {
  useEffect,
  useMemo,
  useState,
} from 'react';
import ActivityIndicator from '@components/common/ActivityIndicator';
import Button from '@components/common/Button';
import Dialog from '@components/common/Dialog';
import Text from '@components/common/Text';
import TextInput from '@components/common/TextInput';
import {
  View,
} from 'react-native';
import {
  getRequestHeaders,
} from '@utils/store/actions';
import getStyles from './styles';
import globals from '@utils/globals';
import hooks from '@hooks';
import utilities from '@utils/utilities';
import {
  v4 as uuidv4,
} from 'uuid';

const NewBibleDialog = (props: NewBibleDialogProps) => {
  const i18n = hooks.useI18n();
  const styles = hooks.useStyles(getStyles);

  const [_translationApiId, setTranslationApiId] = useState('');
  const [_versionText, setVersionText] = useState('');

  const [_recordsRemaining, setRecordsRemaining] = useState(999);

  const [_timestampOfSyncCompletion, setTimestampOfSyncCompletion] = useState(null);
  const [_timestampOfFinalSaveCompletion, setTimestampOfFinalSaveCompletion] = useState(null);

  const batchId = useMemo(() => uuidv4(), []);

  useEffect(() => {
    let timeoutId;

    const poll = async () => {
      const response = await fetch(`${globals.apiUrl}/admin/sync_status/${batchId}`, {
        headers: await getRequestHeaders(),
      });
      const {count_of_remaining_records} = await response.json();

      setRecordsRemaining(count_of_remaining_records);

      if (count_of_remaining_records === 0) {
        setTimestampOfFinalSaveCompletion(Date.now());
        return;
      }

      timeoutId = setTimeout(poll, 5000);
    };

    if (props.syncMessage === globals.timeoutMessage && _recordsRemaining > 0) {
      if (!_timestampOfSyncCompletion) {
        setTimestampOfSyncCompletion(Date.now());
      }
      poll();
    }

    return () => {
      clearTimeout(timeoutId);
    };
  }, [props.syncMessage, batchId, _recordsRemaining, _timestampOfSyncCompletion]);

  return (
    <Dialog
      style={styles.container}
      visible={true}
      onDismiss={() => props.onClose()}
    >
      <Dialog.Title>New Bible</Dialog.Title>
      <Dialog.Content>
        <TextInput
          value={_translationApiId}
          mode='outlined'
          onChangeText={(text) => setTranslationApiId(text)}
          label='API ID'
          placeholder='e.g. c1096b0470327a28-01'
        />
        <TextInput
          value={_versionText}
          mode='outlined'
          onChangeText={(text) => setVersionText(text)}
          label='Available in version'
          placeholder='e.g. 1.2.3'
        />
        {!!props.syncMessage && <Text style={styles.note}>{props.syncMessage}</Text>}
        {_timestampOfSyncCompletion &&
          <React.Fragment>
            <Text style={styles.note}>{`If error message is "${globals.timeoutMessage}", it may just be due to a timeout at the end.  See status below, and make sure the timer does not excede 15 minutes (Lambda hard timeout).`}</Text>
            <View style={styles.loadingProgressRow}>{!_timestampOfFinalSaveCompletion && <ActivityIndicator color={styles.colors.accent} />}<Text style={styles.note}>{_timestampOfFinalSaveCompletion ? `Success! (saving all staged records took ${utilities.humanReadableTimeExact(_timestampOfFinalSaveCompletion - _timestampOfSyncCompletion)})` : `${_recordsRemaining} records remaining after ${utilities.humanReadableTimeExact(Date.now() - _timestampOfSyncCompletion)}`}</Text></View>
          </React.Fragment>
        }
      </Dialog.Content>
      <Dialog.Actions>
        <Button
          mode='text'
          onPress={() => props.onClose()}
        >
          {i18n('cancel')}
        </Button>
        <Button
          disabled={!_translationApiId || !!props.isSyncing}
          loading={!!props.isSyncing}
          onPress={() => props.syncBible(_translationApiId, _versionText, batchId, false, true)}
        >
          {props.isSyncing ? 'Syncing...' : (props.currSyncUrl ? 'Resume sync' : 'Save and sync now')}
        </Button>
      </Dialog.Actions>
    </Dialog>
  );
};

class NewBibleDialogProps {
  onClose: () => void;
  syncBible: (translationAbbreviation: string, version: string, batchId: string, checkStatusOnly: boolean, autoAcceptNew: boolean) => void;
  setSyncMessage: (message: string) => void;
  isSyncing: boolean;

  currSyncUrl?: string;
  syncMessage?: string;
};

export default NewBibleDialog;
